import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@material-ui/core'
import OpalPresentationTable, { usePresentationTableExpandState } from '../../DenaliAnalysis/tables/presentation'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import normalizeQuickBooksReport from './normalizeQuickBooksReport'

const BOLD_ROW_TYPES = new Set(['header', 'summary', 'computed'])

function formatReportName (raw) {
  if (!raw) return ''
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\bAnd\b/g, 'and')
}

function QuickBooksReportDisplay ({ artifact, format = 'marketValue' }) {
  const { formatter } = useFormattingContext()
  const { expandState, setExpandState } = usePresentationTableExpandState(true)

  const report = useMemo(
    () => normalizeQuickBooksReport(artifact?.data),
    [artifact?.data]
  )

  const columns = useMemo(() => {
    if (!report) return []

    return [
      {
        id: 'account',
        header: 'Account',
        accessorFn: row => row.label,
        size: 400,
        cell: ({ row, getValue }) => {
          const isBold = BOLD_ROW_TYPES.has(row.original.rowType)
          const isComputed = row.original.rowType === 'computed'
          const style = {}
          if (isBold) style.fontWeight = 'var(--font-weight-bold, 600)'
          if (isComputed) style.fontStyle = 'italic'
          return <span style={style}>{getValue()}</span>
        }
      },
      ...report.columns.filter(c => c.key !== 'account').map(col => ({
        id: col.key,
        header: col.title || col.key,
        align: 'right',
        accessorFn: row => row.values?.[col.key],
        cell: ({ row, getValue }) => {
          const value = getValue()
          if (value == null) return null

          const isBold = BOLD_ROW_TYPES.has(row.original.rowType)
          const isNegative = typeof value === 'number' && value < 0
          const style = {}
          if (isBold) style.fontWeight = 'var(--font-weight-bold, 600)'
          if (isNegative) style.color = 'var(--color-decline-500)'
          return <span style={style}>{formatter(value, format)}</span>
        }
      }))
    ]
  }, [report, formatter, format])

  if (!report) {
    return (
      <Typography color='textSecondary'>
        No report data available
      </Typography>
    )
  }

  const displayTitle = formatReportName(report.reportName)
  const periodLabel = report.startPeriod && report.endPeriod
    ? `${formatter(report.startPeriod, '@MMM D, YYYY')} to ${formatter(report.endPeriod, '@MMM D, YYYY')}`
    : ''
  const basisLabel = report.reportBasis ? `${report.reportBasis} Basis` : ''
  const subtitle = [basisLabel, report.currency, periodLabel].filter(Boolean).join(' \u00B7 ')

  return (
    <div>
      <div className='mb-3'>
        <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
          {displayTitle}
        </Typography>
        {subtitle ? (
          <Typography variant='caption' color='textSecondary'>
            {subtitle}
          </Typography>
        ) : null}
        {report.noReportData ? (
          <Typography
            variant='body2'
            color='textSecondary'
            className='mt-2'
          >
            No data available for the selected period.
          </Typography>
        ) : null}
      </div>

      {report.rows.length > 0 ? (
        <OpalPresentationTable
          data={report.rows}
          columns={columns}
          className='separated-rows'
          includeHeader
          includeFooter={false}
          expandState={expandState}
          onExpandStateChange={setExpandState}
          emptyStateText='No data in this report'
        />
      ) : null}
    </div>
  )
}

QuickBooksReportDisplay.propTypes = {
  artifact: PropTypes.shape({
    artifactId: PropTypes.number,
    code: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    data: PropTypes.any
  }).isRequired,
  format: PropTypes.string
}

export default QuickBooksReportDisplay
